import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCustom, useCustomMutation, useInvalidate } from "@refinedev/core";
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  TimePicker,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { apiUrlV2 } from "@/consts";
import { fromWallClock, wallClockISO } from "./time";

dayjs.extend(customParseFormat);

const { Title, Text } = Typography;

const GROUP_LABELS: Record<string, string> = {
  academy: "Academy",
  foundation: "Foundation",
  development: "Development",
  performance: "Performance",
};

const PERFORMANCE_GROUPS = Object.entries(GROUP_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const WEEKDAYS = [
  { value: "MON", label: "Mon" },
  { value: "TUE", label: "Tue" },
  { value: "WED", label: "Wed" },
  { value: "THU", label: "Thu" },
  { value: "FRI", label: "Fri" },
  { value: "SAT", label: "Sat" },
  { value: "SUN", label: "Sun" },
];

type SeriesRule = {
  freq: "WEEKLY" | "BIWEEKLY" | "MONTHLY";
  byDays: string[] | null;
  startDate: string;
  timeOfDay: string;
  endTimeOfDay: string;
  endsType: "ON_DATE" | "AFTER_WEEKS" | "END_OF_SEASON";
  endsOn: string | null;
  endsAfterWeeks: number | null;
};

type ScDetail = {
  id: string;
  title: string | null;
  notes: string | null;
  // Every group the class runs for.
  targetIDs: string[];
  startsAt: string;
  endsAt: string;
  seriesID: string | null;
  postCheckinEnabled: boolean;
  series: SeriesRule | null;
};

export function ScScheduleEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const [form] = Form.useForm();
  const scope = Form.useWatch("scope", form) ?? "this";
  const freq = Form.useWatch("freq", form) ?? "WEEKLY";
  const endsType = Form.useWatch("endsType", form) ?? "AFTER_WEEKS";

  const { data, isLoading } = useCustom<{ data: ScDetail }>({
    url: `${apiUrlV2}/schedule/admin/sc/${id}`,
    method: "get",
    queryOptions: { enabled: !!id },
  });
  const detail = data?.data?.data ?? null;
  const recurring = !!detail?.seriesID;

  // S&C times are admin-authored (the FIXED-time policy is bypassed on the admin
  // path), so the form always collects start/end times.

  // Pre-fill the form once the session (and its series rule) loads.
  useEffect(() => {
    if (!detail) return;
    const s = detail.series;
    form.setFieldsValue({
      scope: "this",
      groups: detail.targetIDs,
      title: detail.title ?? "",
      notes: detail.notes ?? "",
      postCheckinEnabled: detail.postCheckinEnabled,
      startsAt: fromWallClock(detail.startsAt),
      endsAt: fromWallClock(detail.endsAt),
      freq: s?.freq ?? "WEEKLY",
      byDays: s?.byDays ?? [],
      timeOfDay: s ? dayjs(s.timeOfDay, "HH:mm") : undefined,
      endTimeOfDay: s ? dayjs(s.endTimeOfDay, "HH:mm") : undefined,
      endsType: s?.endsType ?? "AFTER_WEEKS",
      endsOn: s?.endsOn ? dayjs(s.endsOn) : undefined,
      endsAfterWeeks: s?.endsAfterWeeks ?? undefined,
    });
  }, [detail, form]);

  const { mutate, isLoading: saving } = useCustomMutation();

  function handleFinish(values: any) {
    const patch: Record<string, unknown> = {
      title: values.title?.trim() || null,
      notes: values.notes?.trim() || null,
      postCheckinEnabled: !!values.postCheckinEnabled,
      // The full set the class should run for — the server replaces rather than
      // merges, so deselecting a group removes it.
      target: { targetType: "PERFORMANCE_GROUP", targetIds: values.groups },
    };

    if (values.scope === "all_future") {
      const recurrence: Record<string, unknown> = {
        freq: values.freq,
        endsType: values.endsType,
        timeOfDay: (values.timeOfDay as dayjs.Dayjs).format("HH:mm"),
        endTimeOfDay: (values.endTimeOfDay as dayjs.Dayjs).format("HH:mm"),
      };
      if (values.freq === "WEEKLY" || values.freq === "BIWEEKLY") {
        recurrence.byDays = values.byDays;
      }
      if (values.endsType === "ON_DATE") {
        recurrence.endsOn = (values.endsOn as dayjs.Dayjs).format("YYYY-MM-DD");
      }
      if (values.endsType === "AFTER_WEEKS") {
        recurrence.endsAfterWeeks = values.endsAfterWeeks;
      }
      patch.recurrence = recurrence;
    } else {
      // scope=this — move this single occurrence's window.
      patch.startsAt = wallClockISO(values.startsAt as dayjs.Dayjs);
      patch.endsAt = wallClockISO(values.endsAt as dayjs.Dayjs);
    }

    const qs = values.scope === "all_future" ? "?scope=all_future" : "?scope=this";
    mutate(
      { url: `${apiUrlV2}/schedule/admin/sc/${id}${qs}`, method: "patch", values: patch },
      {
        onSuccess: async () => {
          message.success("S&C session updated");
          await invalidate({ invalidates: ["all"] });
          navigate("/sc-schedule");
        },
        onError: (err: any) =>
          message.error(err?.response?.data?.error || err?.message || "Could not update session"),
      },
    );
  }

  if (isLoading || !detail) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }

  return (
    <Card style={{ maxWidth: 720, margin: "0 auto" }}>
      <Title level={4}>Edit S&amp;C session</Title>
      <Space style={{ marginBottom: 16 }}>
        <Tag color={recurring ? "blue" : "default"}>{recurring ? "Recurring" : "One-off"}</Tag>
        <Text type="secondary">
          {recurring
            ? "\u201cThis occurrence\u201d retargets just this class; \u201cthis & future\u201d retargets the whole series."
            : "Athletes in any selected group see this class."}
        </Text>
      </Space>

      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="groups"
          label="Performance groups"
          rules={[{ required: true, message: "Pick at least one performance group" }]}
        >
          <Select
            mode="multiple"
            options={PERFORMANCE_GROUPS}
            placeholder="Select one or more groups"
          />
        </Form.Item>

        {recurring && (
          <Form.Item name="scope" label="Apply to">
            <Radio.Group
              optionType="button"
              options={[
                { value: "this", label: "This occurrence" },
                { value: "all_future", label: "This & future" },
              ]}
            />
          </Form.Item>
        )}

        <Form.Item name="title" label="Title (optional)">
          <Input maxLength={255} />
        </Form.Item>

        {scope === "all_future" ? (
          <>
            <Space size="large" wrap>
              <Form.Item name="freq" label="Frequency" rules={[{ required: true }]}>
                <Select
                  style={{ width: 160 }}
                  options={[
                    { value: "WEEKLY", label: "Weekly" },
                    { value: "BIWEEKLY", label: "Every 2 weeks" },
                    { value: "MONTHLY", label: "Monthly" },
                  ]}
                />
              </Form.Item>
            </Space>

            {(freq === "WEEKLY" || freq === "BIWEEKLY") && (
              <Form.Item
                name="byDays"
                label="On days"
                rules={[{ required: true, message: "Pick at least one day" }]}
              >
                <Select mode="multiple" options={WEEKDAYS} />
              </Form.Item>
            )}

            <Space size="large" wrap>
              <Form.Item name="timeOfDay" label="Start time" rules={[{ required: true }]}>
                <TimePicker format="HH:mm" minuteStep={5} />
              </Form.Item>
              <Form.Item name="endTimeOfDay" label="End time" rules={[{ required: true }]}>
                <TimePicker format="HH:mm" minuteStep={5} />
              </Form.Item>
            </Space>

            <Form.Item name="endsType" label="Ends" rules={[{ required: true }]}>
              <Select
                style={{ width: 220 }}
                options={[
                  { value: "AFTER_WEEKS", label: "After N weeks" },
                  { value: "ON_DATE", label: "On a date" },
                  { value: "END_OF_SEASON", label: "End of season" },
                ]}
              />
            </Form.Item>
            {endsType === "AFTER_WEEKS" && (
              <Form.Item name="endsAfterWeeks" label="Number of weeks" rules={[{ required: true }]}>
                <InputNumber min={1} max={104} />
              </Form.Item>
            )}
            {endsType === "ON_DATE" && (
              <Form.Item name="endsOn" label="End date" rules={[{ required: true }]}>
                <DatePicker format="YYYY-MM-DD" />
              </Form.Item>
            )}
          </>
        ) : (
          <Space size="large" wrap>
            <Form.Item name="startsAt" label="Starts at" rules={[{ required: true }]}>
              <DatePicker showTime format="YYYY-MM-DD HH:mm" />
            </Form.Item>
            <Form.Item name="endsAt" label="Ends at" rules={[{ required: true }]}>
              <DatePicker showTime format="YYYY-MM-DD HH:mm" />
            </Form.Item>
          </Space>
        )}

        <Form.Item name="notes" label="Notes (optional)">
          <Input.TextArea rows={3} />
        </Form.Item>

        <Form.Item
          name="postCheckinEnabled"
          label="Collect session feedback (check-ins)"
          valuePropName="checked"
          tooltip="Athletes in this performance group can log attendance + feedback after the session."
        >
          <Switch />
        </Form.Item>

        <Space>
          <Button type="primary" htmlType="submit" loading={saving}>
            Save changes
          </Button>
          <Button onClick={() => navigate("/sc-schedule")}>Cancel</Button>
        </Space>
      </Form>
    </Card>
  );
}
